import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import VideoUIAccessError from "@platform/features/videoUI/components/VideoUIAccessError/VideoUIAccessError";
import VideoUIAlreadyInRoomError from "@platform/features/videoUI/components/VideoUIAlreadyInRoomError/VideoUIAlreadyInRoomError";
import VideoUILoading from "@platform/features/videoUI/components/VideoUILoading/VideoUILoading";
import VideoUIEnd from "@platform/features/videoUI/components/VideoUIEnd/VideoUIEnd";
import { useMeetingDetailsOnly } from "../hooks/useMeetingDetailsOnly";
import { useVideo } from "../providers/VideoContext";
import { ProgramDialogs } from "@platform/features/programDialogs";
import useMeetingRelatedUserPhaseAction from "../hooks/useMeetingRelatedUserPhaseAction";

export function VideoEndedPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    meetingId,
    meetingDetails,
    isFetchingMeetingDetails,
    meetingDetailsError,
    fetchMeetingDetails,
  } = useMeetingDetailsOnly();

  const { roomAccessError, lastHeartbeat } = useVideo();

  const { meetingId: meetingIdFromUrl } = useParams();

  // Auto-refresh after 0.5 seconds to ensure device cleanup (only if not already refreshed)
  useEffect(() => {
    const hasRefreshed = searchParams.get("refreshed") === "true";

    if (!hasRefreshed) {
      const timer = setTimeout(() => {
        console.log(
          "Auto-refreshing video end page to ensure device cleanup..."
        );
        // Add query parameter to indicate we've refreshed and reload
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("refreshed", "true");
        window.location.replace(currentUrl.toString());
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  useEffect(() => {
    // If no meetingId in URL and no meetingId in context, redirect to dashboard
    if (!meetingIdFromUrl && !meetingId) {
      navigate("/dashboard");
      return;
    }

    // If we have a meetingId from URL, fetch meeting details
    if (meetingIdFromUrl && meetingIdFromUrl !== meetingId) {
      fetchMeetingDetails(meetingIdFromUrl);
    }
  }, [meetingIdFromUrl, meetingId, navigate, fetchMeetingDetails]);

  const meetingSurveyAction =
    useMeetingRelatedUserPhaseAction(
      meetingIdFromUrl || meetingId,
      "meeting_survey",
      meetingDetails?.program ?? 0
    ) || null;

  // Show loading state
  if (isFetchingMeetingDetails) {
    return <VideoUILoading />;
  }

  // Show "already in room" error state
  if (roomAccessError === "ALREADY_IN_ROOM") {
    return <VideoUIAlreadyInRoomError lastHeartbeat={lastHeartbeat} />;
  }

  // Show other error state
  if (meetingDetailsError) {
    return <VideoUIAccessError errorMessage={meetingDetailsError} />;
  }

  // Show device setup for meeting details available
  if (meetingDetails) {
    return (
      <>
        <VideoUIEnd
          meetingDetails={meetingDetails}
          meetingSurveyAction={meetingSurveyAction}
        />
        <ProgramDialogs />
      </>
    );
  }
}

export default VideoEndedPage;
